import { useLanguage } from "~/composables/useLanguage";

// 검증 규칙 상수
export const VALIDATION_RULES = {
	// 아이디 규칙
	loginId: {
		minLength: 4,
		maxLength: 20,
		pattern: /^[a-zA-Z0-9_-]+$/
	},

	// 비밀번호 규칙
	password: {
		minLength: 8,
		maxLength: 20,
		pattern: /^(?=.*[a-zA-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]+$/
	},

	// 이름 규칙
	name: {
		minLength: 2,
		maxLength: 20,
		pattern: /^[가-힣a-zA-Z\s]+$/
	},

	// 휴대폰번호 규칙
	phone: {
		pattern: /^01[016789]-?\d{3,4}-?\d{4}$/
	},

	// 이메일 규칙
	email: {
		pattern: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/
	},
	emailLocal: {
		pattern: /^[a-zA-Z0-9._%+-]+$/, // @ 앞부분만
		minLength: 1,
		maxLength: 64
	},
	emailDomain: {
		pattern: /^[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/, // @ 뒷부분만
		minLength: 4,
		maxLength: 253
	}
}

// 검증 결과 타입
interface ValidationResult {
	isValid: boolean
	message: string
}


export const useValidation = () => {
	const { lang } = useLanguage();

	/**
	 * 로그인 아이디 검증
	 * @param value	입력받은 로그인 아이디
	 */
	const validateLoginId = (value: string): ValidationResult => {
		const trimmedValue = value.trim();
		const rule = VALIDATION_RULES.loginId;

		// 빈 값 체크
		if (!trimmedValue) {
			// return { isValid: false, message: lang.validation.required('loginId') };
			return { isValid: false, message: 'validation.required.loginId' };
		}

		// 길이(min/max) 또는 형식 불일치 시 모두 동일한 포맷 메시지로 통일
		if (
			trimmedValue.length < rule.minLength ||
			trimmedValue.length > rule.maxLength ||
			!rule.pattern.test(trimmedValue)
		) {
			return { isValid: false, message: lang.validation.format('loginId') };
		}

		// 모두 통과
		return { isValid: true, message: '' };
	}

	/**
	 * 비밀번호 검증
	 * @param value	입력받은 비밀번호
	 */
	const validatePassword = (value: string): ValidationResult => {
		const rule = VALIDATION_RULES.password;

		// 빈 값 체크
		if (!value) {
			return { isValid: false, message: 'validation.required.password' };
			// return { isValid: false, message: lang.validation.required('password') };
		}

		// 길이(min/max) 또는 형식 불일치 시 모두 동일한 포맷 메시지로 통일
		if (
			value.length < rule.minLength ||
			value.length > rule.maxLength ||
			!rule.pattern.test(value)
		) {
			return { isValid: false, message: lang.validation.format('password') };
		}

		// 모두 통과
		return { isValid: true, message: '' };
	};

	/**
	 * 비밀번호 확인 검증
	 * @param password			원본 비밀번호
	 * @param passwordConfirm	확인용 비밀번호
	 */
	const validatePasswordConfirm = (password: string, passwordConfirm: string): ValidationResult => {
		if (!passwordConfirm) {
			return { isValid: false, message: lang.validation.required('passwordConfirm') };
		}

		if (password !== passwordConfirm) {
			return { isValid: false, message: lang.validation.match('passwordMismatch') };
		}

		return { isValid: true, message: '' };
	}

	/**
	 * 이름 검증
	 * @param value	검증할 이름
	 */
	const validateName = (value: string): ValidationResult => {
		const trimmedValue = value.trim();
		const rule = VALIDATION_RULES.name;

		// 빈 값 체크
		if (!trimmedValue) {
			return { isValid: false, message: 'validation.required.name' };
		}

		// 길이(min/max) 또는 형식 불일치 시 모두 동일한 포맷 메시지로 통일
		if (
			trimmedValue.length < rule.minLength ||
			trimmedValue.length > rule.maxLength ||
			!rule.pattern.test(trimmedValue)
		) {
			return { isValid: false, message: 'validation.format.name' };
		}

		// 모두 통과
		return { isValid: true, message: '' };
	};

	/**
	 * 직급 검증 (선택사항)
	 * @param value	검증할 직급
	 */
	const validatePosition = (value: string): ValidationResult => {
		const trimmedValue = value.trim()

		if (trimmedValue && trimmedValue.length > 20) {
			return {
				isValid: false,
				message: lang.validation.length('positionMax', { max: 20 })
			}
		}

		return { isValid: true, message: '' }
	}

	/**
	 * 휴대폰 번호 검증
	 * @param value	검증할 휴대폰 번호
	 */
	const validatePhone = (value: string): ValidationResult => {
		const cleanValue = value.replace(/[-\s]/g, ''); // 하이픈 및 공백 제거
		const rule = VALIDATION_RULES.phone;

		if (!cleanValue) {
			return { isValid: false, message: 'validation.required.phone' };
		}

		if (!rule.pattern.test(cleanValue)) {
			return { isValid: false, message: 'validation.format.phone' };
		}

		return { isValid: true, message: '' };
	}

	/**
	 * 전체 이메일 검증
	 * @param value	입력받은 이메일
	 */
	const validateEmail = (value: string | null): ValidationResult => {
		if (value === null) {
			return { isValid: false, message: lang.validation.required('email') }
		}
		const trimmedValue = value.trim();
		const rule = VALIDATION_RULES.email;

		if (!trimmedValue) {
			return { isValid: false, message: lang.validation.required('email') };
		}

		if (!rule.pattern.test(trimmedValue)) {
			return { isValid: false, message: lang.validation.format('email') };
		}

		return { isValid: true, message: '' };
	}

	/**
	 * 이메일 로컬 부분 검증 (도메인 앞부분)
	 * @param value	입력받은 이메일 앞부분
	 */
	const validateEmailLocal = (value: string | null): ValidationResult => {
		if (value === null) {
			return { isValid: false, message: 'validation.required.emailLocal' }
		}
		const trimmedValue = value.trim()
		const rule = VALIDATION_RULES.emailLocal

		if (!trimmedValue) {
			return { isValid: false, message: 'validation.required.emailLocal' }
		}

		// 길이(min/max) 또는 형식 불일치 시 모두 동일한 포맷 메시지로 통일
		if (
			trimmedValue.length < rule.minLength ||
			trimmedValue.length > rule.maxLength ||
			!rule.pattern.test(trimmedValue)
		) {
			return { isValid: false, message: 'validation.format.emailLocal' }
		}

		return { isValid: true, message: '' }
	}

	/**
	 * 이메일 도메인 부분 검증 (도메인 뒷부분)
	 * @param value	입력받은 이메일 뒷부분
	 */
	const validateEmailDomain = (value: string | null): ValidationResult => {
		if (value === null) {
			return { isValid: false, message: 'validation.required.emailDomain' }
		}
		const trimmedValue = value.trim()
		const rule = VALIDATION_RULES.emailDomain

		if (!trimmedValue) {
			return { isValid: false, message: 'validation.required.emailDomain' }
		}

		// 길이(min/max) 또는 형식 불일치 시 모두 동일한 포맷 메시지로 통일
		if (
			trimmedValue.length < rule.minLength ||
			trimmedValue.length > rule.maxLength ||
			!rule.pattern.test(trimmedValue)
		) {
			return { isValid: false, message: 'validation.format.emailDomain' }
		}

		return { isValid: true, message: '' }
	}

	/**
	 * 전체 이메일 검증 (로컬 + 도메인)
	 * @param localPart		이메일 로컬부분
	 * @param domainPart	이메일 도메인부분
	 */
	const validateFullEmail = (localPart: string, domainPart: string | null): ValidationResult => {
		if( !localPart || !domainPart) {
			return { isValid: false, message: lang.validation.required('email') }
		}

		// 각 부분 개별 검증
		const localResult = validateEmailLocal(localPart)
		if (!localResult.isValid) {
			return localResult
		}

		const domainResult = validateEmailDomain(domainPart)
		if (!domainResult.isValid) {
			return domainResult
		}

		// 전체 이메일 형태 검증
		const fullEmail = `${localPart.trim()}@${domainPart.trim()}`
		const emailResult = validateEmail(fullEmail)
		if (!emailResult.isValid) {
			return { isValid: false, message: lang.validation.format('email') }
		}

		return { isValid: true, message: '' }
	}

	/**
	 * 인증 코드 검증
	 * @param value	입력받은 인증 코드
	 */
	const validateVerificationCode = (value: string): ValidationResult => {
		if (!value) {
			return { isValid: false, message: 'validation.required.verificationCode' };
		}

		// 길이나 형식 불일치 시 모두 동일한 포맷 메시지로 통일
		if (!/^\d{6}$/.test(value)) {
			return { isValid: false, message: 'validation.format.verificationCode' };
		}

		return { isValid: true, message: '' };
	};

	/**
	 * 회원가입 폼 검증
	 * @param formData	입력한 회원가입 데이터
	 */
	const validateSignUpForm = (formData: {
		loginId: string,
		password: string,
		passwordConfirm: string,
		name: string,
		position?: string,
		phone: string,
		emailLocal: string,      // 🔄 email → emailLocal, emailDomain으로 분리
		emailDomain: string | null,     // 🔄 추가된 필드
		isIdChecked: boolean
		isIdAvailable: boolean
		isTermsAgreed: boolean
	}) => {
		const errors: Record<string, string> = {};

		// 아이디 검증
		if(!formData.isIdChecked || !formData.isIdAvailable) {
			errors.loginId = lang.validation.duplicate('loginIdCheck');
		} else {
			const loginIdResult = validateLoginId(formData.loginId);
			if (!loginIdResult.isValid) {
				errors.loginId = loginIdResult.message;
			}
		}

		// 비밀번호 검증
		const passwordResult = validatePassword(formData.password);
		if (!passwordResult.isValid) {
			errors.password = passwordResult.message;
		}

		// 비밀번호 확인 검증
		const passwordConfirmResult = validatePasswordConfirm(formData.password, formData.passwordConfirm);
		if (!passwordConfirmResult.isValid) {
			errors.passwordConfirm = passwordConfirmResult.message;
		}

		// 이름 검증
		const nameResult = validateName(formData.name);
		if (!nameResult.isValid) {
			errors.name = nameResult.message;
		}

		// 직급 검증 (선택사항)
		if (formData.position) {
			const positionResult = validatePosition(formData.position)
			if (!positionResult.isValid) {
				errors.position = positionResult.message
			}
		}

		// 휴대폰 번호 검증
		const phoneResult = validatePhone(formData.phone);
		if (!phoneResult.isValid) {
			errors.phone = phoneResult.message;
		}

		// 🔄 이메일 검증 (분리된 로컬/도메인 방식으로 변경)
		const emailResult = validateFullEmail(formData.emailLocal, formData.emailDomain);
		if (!emailResult.isValid) {
			errors.email = emailResult.message;
		}

		// 약관 동의 검증
		if (!formData.isTermsAgreed) {
			errors.terms = lang.validation.terms('privacyRequired');
		}

		return {
			isValid: Object.keys(errors).length === 0,
			errors
		}
	}

	const formatPhoneNumber = (value: string): string => {
		const cleaned = value.replace(/\D/g, ''); // 숫자만 남김

		if( cleaned.length <= 3) {
			return cleaned; // 너무 짧으면 그냥 반환
		} else if (cleaned.length <= 7) {
			return `${cleaned.slice(0, 3)}-${cleaned.slice(3)}`; // 3-4 자리
		} else {
			return `${cleaned.slice(0, 3)}-${cleaned.slice(3, 7)}-${cleaned.slice(7)}`; // 3-4-4 자리
		}
	}

	/**
	 * 실시간 검증 헬퍼 (디바운스 적용)
	 */
	let validationTimeout: NodeJS.Timeout | null = null

	const validateFieldWithDelay = (
		validator: (value: string) => ValidationResult,
		value: string,
		callback: (result: ValidationResult) => void,
		delay = 300
	) => {
		if (validationTimeout) {
			clearTimeout(validationTimeout)
		}

		validationTimeout = setTimeout(() => {
			const result = validator(value)
			callback(result)
		}, delay)
	}



	return {
		// 검증 함수들
		validateLoginId,
		validatePassword,
		validatePasswordConfirm,
		validateName,
		validatePosition,
		validatePhone,
		validateEmail,
		validateSignUpForm,
		validateEmailLocal,
		validateEmailDomain,
		validateFullEmail,
		validateVerificationCode,

		// 유틸리티 함수들
		formatPhoneNumber,
		validateFieldWithDelay,

		// 상수
		VALIDATION_RULES
	}
}